import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useBookings, useGuestSearch } from '../api/queries';
import { BOOKING_STATUS, formatDate } from '../format';
import { Badge, Empty, ErrorNote, Field, Loading, Modal } from '../ui/kit';
import { PageHead } from './shared';

interface GuestDetail {
  id: string;
  fullName: string;
  phone: string | null;
  email: string | null;
  nationality: string | null;
  idType: string | null;
  idNumber: string | null;
  idNumberMasked: string | null;
  notes: string | null;
  messagingOptOut?: boolean;
}

export function Guests() {
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const found = useGuestSearch(search);

  return (
    <div className="page">
      <PageHead eyebrow="Front desk" title="Guests" />
      <Field id="gq" label="Search by name or phone" hint="Type at least two letters">
        <input id="gq" className="input" value={search} onChange={(e) => setSearch(e.target.value)} autoComplete="off" autoFocus />
      </Field>
      {search.trim().length < 2 ? (
        <div className="card flat">
          <Empty icon="search" title="Find a guest">
            Search by name or phone number to see their details and stays.
          </Empty>
        </div>
      ) : found.isLoading ? (
        <Loading rows={2} />
      ) : found.error ? (
        <ErrorNote error={found.error} retry={() => void found.refetch()} />
      ) : (found.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="search" title="No guest found" />
        </div>
      ) : (
        <div className="list">
          {found.data!.map((g) => (
            <button key={g.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setOpenId(g.id)}>
              <span className="title">{g.fullName}</span>
              <span className="small muted">{[g.phone, g.email].filter(Boolean).join(' · ') || 'No contact details'}</span>
            </button>
          ))}
        </div>
      )}
      <GuestDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}

function GuestDialog({ id, onClose }: { id: string | null; onClose: () => void }) {
  const { can } = useAuth();
  const g = useQuery({ queryKey: ['guest', id], enabled: !!id, queryFn: () => api<GuestDetail>(`/guests/${id}`) });
  const qc = useQueryClient();
  const optOut = useMutation({ mutationFn: (v: boolean) => api(`/guests/${id}`, { method: 'PATCH', body: { messagingOptOut: v } }), onSuccess: () => qc.invalidateQueries({ queryKey: ['guest', id] }) });
  const stays = useBookings({ ...(id ? { guestId: id } : {}), limit: 20 } as never, !!id && can('booking.view'));
  return (
    <Modal open={!!id} onClose={onClose} title={g.data?.fullName ?? 'Guest'}>
      {g.isLoading ? (
        <Loading rows={2} />
      ) : g.error ? (
        <ErrorNote error={g.error} retry={() => void g.refetch()} />
      ) : g.data ? (
        <div className="stack">
          <dl className="kv">
            <dt>Phone</dt>
            <dd>{g.data.phone ?? '–'}</dd>
            <dt>Email</dt>
            <dd>{g.data.email ?? '–'}</dd>
            <dt>Nationality</dt>
            <dd>{g.data.nationality ?? '–'}</dd>
            <dt>ID</dt>
            <dd>{g.data.idType ? `${g.data.idType.replace('_', ' ').toLowerCase()} ` : ''}{g.data.idNumber ?? g.data.idNumberMasked ?? '–'}</dd>
          </dl>
          {g.data.idNumber ? <p className="hint">Viewing an ID number is recorded in the audit log.</p> : null}
          {g.data.notes ? <p className="small muted">{g.data.notes}</p> : null}
          {can('guest.manage') ? (
            <label className="choice">
              <input type="checkbox" checked={!!g.data.messagingOptOut} disabled={optOut.isPending} onChange={(e) => optOut.mutate(e.target.checked)} />
              <span>Does not want automatic messages (confirmations, reminders)</span>
              <span />
            </label>
          ) : g.data.messagingOptOut ? <p className="hint">This guest declined automatic messages.</p> : null}
          <div>
            <div className="eyebrow" style={{ marginBottom: 6 }}>
              Stays
            </div>
            {stays.isLoading ? (
              <Loading rows={1} />
            ) : (stays.data ?? []).length === 0 ? (
              <p className="small muted">No stays on record.</p>
            ) : (
              <div className="list">
                {stays.data!.map((b) => (
                  <Link key={b.id} to={`/bookings/${b.id}`} className="item" onClick={onClose}>
                    <div className="row" style={{ justifyContent: 'space-between' }}>
                      <span className="num">{b.reference}</span>
                      <Badge tone={BOOKING_STATUS[b.status]?.tone ?? ''}>{BOOKING_STATUS[b.status]?.label ?? b.status}</Badge>
                    </div>
                    <span className="small muted">
                      {formatDate(b.checkIn)} → {formatDate(b.checkOut)}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
